/**
 * Ereignisse sind ein dauerhaftes Protokoll für die Synchronisation (Leitlinie, Seite 8). Jedes Ereignis gehört
 * zu genau einem Strom (`scope`). Die laufende Nummer `seq` ist innerhalb des Stroms streng monoton und entspricht
 * der Commit-Reihenfolge. Inhalte, die nicht jede Person im Strom sehen darf, gehören nie in `payload`.
 */
export type ScopeType = 'workspace' | 'project';

export interface Scope {
  type: ScopeType;
  id: string;
}

export const EVENT_TYPES = [
  'settings.changed',
  'profile.changed',
  'workspace.updated',
  'member.changed',
  'member.removed',
  'invitation.changed',
  'project.updated',
  'project.member.changed',
  'project.member.removed',
  'list.changed',
  'list.removed',
  'list.merged',
  'status.changed',
  'status.rule.changed',
  'board.ordered',
  'status.cleared',
  'status.removed',
  'task.changed',
  'task.removed',
  'comment.changed',
  'comment.removed',
  'attachment.changed',
  'attachment.removed',
  'section.changed',
  'section.removed',
  'label.changed',
  'label.removed',
  'field.changed',
  /** P25: geteilte gespeicherte Ansicht geändert/entfernt (Payload nur die ID) */
  'view.changed',
  // 0.152 (ADR 0154): Tags/Ansichten der Ablage geändert (nur Invalidierung)
  'tags.changed',
  'view.removed',
  'field.removed',
  'share.changed',
  'checklist.changed',
  'checklist.removed',
  'file.changed',
  'file.removed',
  'folder.changed',
  'folder.removed',
  'page.changed',
  'page.removed',
  'page.comment.changed',
  'page.comment.removed',
  'talk.topic.changed',
  'talk.topic.removed',
  'talk.post.changed',
  'talk.post.removed',
  'talk.message.changed',
  'talk.message.removed',
  'area.changed',
  'area.removed',
  /** P15: Sichtbarkeit, Teams oder Bereichszuordnung geändert – Clients laden den Projektstand neu */
  'access.changed',
  /** Audit F07: Seite, Datei oder Thema an die Übersicht geheftet/gelöst (Payload: kind, targetId, on) */
  'page.pinned',
  'file.pinned',
  'talk.pinned',
  /** Audit F10: Verknüpfung gesetzt/gelöst – je Ende ein Ereignis mit dessen Bereich (Payload: kind, id) */
  'link.changed',
  /** Audit 0.36 Etappe C: Abnahme angefragt, entschieden oder zurückgezogen (Payload: ApprovalDto; Bereich des Inhalts) */
  'approval.changed',
  // ADR 0086: Freigabeverfahren (Lauf über Stufen)
  'approval.run.changed',
  'file.comment.changed',
  // ADR 0064: Projekteingang (nie an Gäste zugestellt)
  'intake.changed',
  'event.changed',
  'event.removed',
  // ADR 0077: Entscheidungsanfragen
  'decision.changed',
  // ADR 0080: Personen und Anmeldungen (Nutzlast nur Kennungen – Daten lädt, wer sie sehen darf)
  'people.changed',
  // ADR 0082: Budget (nie an Gäste; Nutzlast mit Beträgen nur für Bearbeitende des Bereichs)
  'budget.changed',
  // ADR 0083: Ressourcen/Buchungen und Einsätze (nur Bearbeitende; Helfer laden ihre eigenen Einsätze gezielt)
  'resource.changed',
  'shift.changed',
  // ADR 0084: Ziele & Risiken (nie an Gäste)
  'goal.changed',
  'risk.changed',
  // ADR 0085: Zeit – Nutzlast nur Kennungen, nie im Verlauf (Zeiten sind persönlich)
  'time.changed',
  'intake.form.changed',
  // ADR 0066: Regel-Lauf (nur Leitung/Mitglieder mit „Bearbeiten“)
  'automation.run',
  'automation.changed',
  // ADR 0125: Papierkorb (Arbeitsgruppen-Strom, nur Kennung – Lobby lädt neu)
  'project.trashed',
  'project.purged',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export interface EventDto {
  scope: Scope;
  seq: number;
  type: EventType;
  entityId: string | null;
  opId: string | null;
  actorId: string | null;
  /** Nur Daten, die alle Abonnenten des Stroms sehen dürfen */
  payload: unknown;
  createdAt: string;
}

export function scopeKey(s: Scope): string {
  return `${s.type}:${s.id}`;
}
